import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Spin,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import Moment from 'moment';
import { useSelector } from 'react-redux';
import { getVariable } from 'api/configuracion/variables';
import { MAX_LONGITUD_TEXTO } from 'api/procesosMasivos/descuentosMasivos';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import NumericInput from 'components/NumericInput';

const TIPOS_DE_VARIABLE = Object.freeze({
  ENTERO: 1,
  DECIMAL: 2,
  RANGO_DE_FECHAS: 3,
});

const getTipoDeVariable = (variable) => variable?.tipo_de_variable?.id
?? variable?.tipo_de_variable;
const getVariableId = (variable) => variable?.id ?? variable;

/**
 * Valor que espera el backend por variable (texto). El rango de fechas se envía en días,
 * igual que en la captura manual de descuentos especiales.
 */
const formatValor = (variable, valor) => {
  if (getTipoDeVariable(variable) === TIPOS_DE_VARIABLE.RANGO_DE_FECHAS) {
    const [fechaInicial, fechaFinal] = valor;
    return String(fechaFinal.diff(fechaInicial, 'days'));
  }
  return String(valor);
};

const ModalCapturaDescuentos = ({
  visible,
  onRegresar,
  onAplicar,
  descuentos,
}) => {
  const [form] = Form.useForm();
  const tiposDeAplicacion = useSelector(({ catalogs }) => catalogs.tiposDeAplicacion) || [];
  const [loading, setLoading] = useState(false);
  // { [tipoDeDescuentoId]: Array<variable> }
  const [variablesPorDescuento, setVariablesPorDescuento] = useState({});
  // Variables ya leídas (id -> variable): el modal vive mientras la pantalla está abierta,
  // así que al volver a abrirlo sólo se piden las que falten
  const variablesLeidas = useRef(new Map());

  useEffect(() => {
    let mounted = true;
    const fetchVariables = async () => {
      setLoading(true);
      form.resetFields();
      const idsPorDescuento = descuentos.map((descuento) => [
        descuento.id,
        (descuento.variables || []).map(getVariableId),
      ]);
      const faltantes = [...new Set(idsPorDescuento.flatMap(([, ids]) => ids))]
        .filter((id) => !variablesLeidas.current.has(id));
      const leidas = await Promise.all(faltantes.map((id) => getVariable(id)));
      leidas.forEach((variable, idx) => {
        if (variable) variablesLeidas.current.set(faltantes[idx], variable);
      });
      if (mounted) {
        setVariablesPorDescuento(Object.fromEntries(idsPorDescuento.map(([descuentoId, ids]) => [
          descuentoId,
          ids.map((id) => variablesLeidas.current.get(id)).filter(Boolean),
        ])));
        setLoading(false);
      }
    };
    if (visible) fetchVariables();
    return () => { mounted = false; };
  }, [visible]);

  const onFinish = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const capturados = descuentos.map((descuento) => ({
      tipo_de_descuento: descuento.id,
      variables: (variablesPorDescuento[descuento.id] || []).map((variable) => ({
        nombre_de_variable: variable.nombre_de_variable,
        valor:
        formatValor(variable, values.variables?.[descuento.id]?.[variable.nombre_de_variable]),
      })),
    }));
    onAplicar({
      descuentos: capturados,
      comentarios: values.comentarios?.trim() || null,
    });
  };

  const renderVariable = (descuento, variable) => {
    const name = ['variables', descuento.id, variable.nombre_de_variable];
    const tipo = getTipoDeVariable(variable);
    if (tipo === TIPOS_DE_VARIABLE.RANGO_DE_FECHAS) {
      return (
        <Form.Item
          label={variable.descripcion_de_variable}
          name={name}
          rules={[{ required: true, message: 'Seleccione el rango de fechas' }]}
        >
          <DatePicker.RangePicker
            format="DD/MM/YYYY"
            disabledDate={(current) => current && current < Moment().startOf('day')}
          />
        </Form.Item>
      );
    }
    return (
      <NumericInput
        label={variable.descripcion_de_variable}
        name={name}
        required
        decimal={tipo === TIPOS_DE_VARIABLE.DECIMAL}
      />
    );
  };

  return (
    <Modal
      open={visible}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={!loading}
      width={600}
      centered
      title={(
        <FormSubmitCustomControls
          label="Descuentos Masivos"
          loading={loading}
          controls={[
            {
              text: 'Regresar',
              icon: <ArrowLeftOutlined />,
              onClick: onRegresar,
            },
            {
              text: 'Aplicar descuento',
              icon: <PlusOutlined />,
              onClick: onFinish,
            },
          ]}
        />
      )}
    >
      <Spin tip="Cargando..." spinning={loading}>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          {descuentos.map((descuento) => {
            const variables = variablesPorDescuento[descuento.id] || [];
            const tipoDeAplicacion = tiposDeAplicacion
              .find((t) => t.id === descuento.tipo_de_aplicacion)?.descripcion;
            return (
              <React.Fragment key={descuento.id}>
                <Typography.Text strong>
                  {`${descuento.clave} - ${descuento.descripcion}`}
                </Typography.Text>
                <br />
                <Typography.Text type="secondary">
                  {variables.length
                    ? tipoDeAplicacion
                    : `${tipoDeAplicacion || 'Tipo de aplicación'}: se aplica con la configuración del catálogo`}
                </Typography.Text>
                {!!variables.length && (
                  <Row gutter={10} style={{ marginTop: 10 }}>
                    {variables.map((variable) => (
                      <Col xs={24} sm={12} key={variable.id}>
                        {renderVariable(descuento, variable)}
                      </Col>
                    ))}
                  </Row>
                )}
                <Divider style={{ margin: '12px 0' }} />
              </React.Fragment>
            );
          })}
          <Form.Item
            name="comentarios"
            label="Observaciones"
            tooltip="Registre quién autoriza el descuento y los datos que considere necesarios. Se guarda en el estado de cuenta de cada cuenta."
            rules={[{
              required: true,
              whitespace: true,
              message: 'Capture quién autoriza el descuento',
            }]}
          >
            <Input.TextArea
              autoSize={{ minRows: 3, maxRows: 5 }}
              maxLength={MAX_LONGITUD_TEXTO}
              showCount
              allowClear
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

ModalCapturaDescuentos.propTypes = {
  visible: PropTypes.bool.isRequired,
  onRegresar: PropTypes.func.isRequired,
  onAplicar: PropTypes.func.isRequired,
  descuentos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    clave: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    descripcion: PropTypes.string,
    tipo_de_aplicacion: PropTypes.number,
    variables: PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.shape({}),
    ])),
  })).isRequired,
};

export default ModalCapturaDescuentos;
